import { Avatar, Button, Typography } from '@/components/common';
import { InputText } from '@/components/form';
import Icons from '@/const/icons';
import { PaymentMethodEnum, UserToPayItem } from '@/interfaces';
import { formatUSD } from '@/lib';
import { WarningShield } from '@/public/icons/tsx';
import * as React from 'react';
import { useForm } from 'react-hook-form';

interface PayConfirmModalProps {
	paymentMethod: PaymentMethodEnum | undefined;
	users: UserToPayItem[] | undefined;
	confirmPaySettlement: () => void;
	hideModal: () => void;
	totalSettlementAmount: number;
}

export const PayConfirmModal: React.FC<PayConfirmModalProps> = ({
	paymentMethod,
	users,
	confirmPaySettlement,
	hideModal,
	totalSettlementAmount,
}) => {
	const { register, watch } = useForm({ mode: 'onChange' });

	return (
		<div>
			<div className="bg-grey-5 p-6 rounded-2xl space-y-3">
				<Typography type="semibold-16" className="text-left">
					Payment Summary
				</Typography>
				<div className="flex items-center justify-between">
					<Typography type="regular-14" className="text-opacity-black">
						Total Customers:
					</Typography>
					<Typography type="semibold-14" className="text-black">
						{users?.length ?? 0}
					</Typography>
				</div>
				<div className="flex items-center justify-between">
					<Typography type="regular-14" className="text-opacity-black">
						Payment Method
					</Typography>
					<Typography type="semibold-14" className="text-black">
						{paymentMethod === PaymentMethodEnum.INDIVIDUAL
							? 'Individually'
							: 'Group'}
					</Typography>
				</div>
				{paymentMethod === PaymentMethodEnum.GROUP && (
					<div className="flex items-center justify-between">
						<Typography type="regular-14" className="text-opacity-black">
							Per Plaintiff Total:
						</Typography>
						<Typography type="semibold-14" className="text-black">
							${formatUSD(users?.[0]?.amount ?? 0)}
						</Typography>
					</div>
				)}
				<div className="flex items-center justify-between border-t border-gray-disabled pt-3">
					<Typography type="semibold-16">Settlement Ammount:</Typography>
					<Typography type="semibold-16" className="text-blue-link">
						${formatUSD(totalSettlementAmount)}
					</Typography>
				</div>
			</div>

			{paymentMethod === PaymentMethodEnum.INDIVIDUAL && (
				<div className="mt-8 border border-grey-100 rounded-2xl">
					<div className="bg-gray-15 py-2 px-6 rounded-t-2xl">
						<Typography type="regular-14" className="text-left">
							Client List
						</Typography>
					</div>
					<ul className="max-h-[452px] overflow-y-auto scroll-custom">
						{users?.map((user) => (
							<li
								key={user.application_id}
								className="flex items-center justify-between py-2 px-6 border-t border-grey-100"
							>
								<div className="flex items-center gap-2">
									<Avatar
										photoUrl={user.user_profile_picture ?? ''}
										firstname={user.user_firstname}
										lastname={user.user_lastname}
										size="extra-small"
									/>
									<Typography type="regular-14" className="text-opacity-black">
										{user.user_firstname} {user.user_lastname}
									</Typography>
								</div>
								<div className="border border-gray-disabled rounded-full px-6 py-1 w-[140px]">
									<Typography type="regular-16" className="text-opacity-black">
										${formatUSD(user.amount ?? 0)}
									</Typography>
								</div>
							</li>
						))}
					</ul>
				</div>
			)}

			<div className="space-y-2 text-left mt-4">
				<Typography type="regular-16">Security Veritifaction</Typography>
				<Typography type="regular-14" className="text-opacity-black">
					Type <span className="text-black">“Pay Settlement”</span> to confirm
					this action
				</Typography>
				<InputText
					register={register}
					name="pay"
					placeholder="Pay Settlement"
					classNameInput="!bg-grey-5"
				/>
			</div>

			<div className="flex items-center gap-x-5 bg-[#FD983426] rounded-2xl p-3 my-4">
				<WarningShield size={50} className="!text-main-orange" />
				<div className="text-left">
					<Typography type="semibold-14">Important</Typography>
					<Typography type="regular-14" className="text-grey-60">
						This action will process payments to all {users?.length} customers.
						This action cannot be undone once confirmed.
					</Typography>
				</div>
			</div>

			<div className="flex justify-center items-center gap-x-4">
				<Button
					label="Cancel"
					size="fit"
					decoration="danger-outline"
					onClick={hideModal}
				/>
				<Button
					label="Confirm Settlement"
					size="fit"
					decoration="primary"
					onClick={confirmPaySettlement}
					icon={Icons.lockWhite}
					iconLeft
					classNameButton="!size-[14px]"
					disabled={watch('pay') !== 'Pay Settlement'}
				/>
			</div>
		</div>
	);
};
